//lib/auth-context.tsx
'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { clearTenantStorage, getActiveTenantSlug, saveTenantSlug } from './tenant-storage';

import { fetchWithAuth } from './fetchWithAuth';
interface TokenPayload {
  sub: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  jobTitle?: string | null;
  role: 'USER' | 'ADMIN' | 'OPERATOR';
  exp: number;
  isTwoFactorEnabled: boolean;
  logoUrl?: string | null;
  tintElements?: string | null;
  tintText?: string | null;
  tintBackground?: string | null;
  tenantId?: number | null;
  tenantSlug?: string | null;
  tenantName?: string | null;
  hasCompletedOnboarding?: boolean;
}

async function parseSwitchError(res: Response): Promise<Error> {
  try {
    const data = await res.json();
    const msg = Array.isArray(data?.message) ? data.message.join(', ') : data?.message;
    return new Error(msg || `Could not switch tenant (${res.status})`);
  } catch {
    return new Error(`Could not switch tenant (${res.status})`);
  }
}

interface AuthContextProps {
  isAuthenticated: boolean;
  token: string | null;
  user: TokenPayload | null;
  login: (token: string, options?: { reconcileTenant?: boolean }) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  /**
   * The active tenant lives in the JWT, so it can only change by minting a new
   * one. Anything that claims to "open" another workspace must call this —
   * links carrying ?tenant=<slug> changed the URL and nothing else.
   */
  switchTenant: (slug: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  token: null,
  user: null,
  login: async () => { },
  logout: () => { },
  switchTenant: async () => { },
  refreshAccessToken: async () => { },
});

let accessToken: string | null = null;

export const getAccessToken = () => accessToken;

export const setAccessToken = (newToken: string | null) => {
  accessToken = newToken;
  if (typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new Event('tokenUpdated'));
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<TokenPayload | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [checking, setChecking] = useState(true); // ⏳ Wait for refresh before rendering

  const normalizeRole = (raw: unknown): TokenPayload['role'] | null => {
    if (raw === 'SUPERADMIN') return 'OPERATOR';
    if (raw === 'OPERATOR' || raw === 'ADMIN' || raw === 'USER') return raw;
    return null;
  };

  const setToken = (newToken: string | null) => {
    setAccessToken(newToken);
    setTokenState(newToken);
    if (newToken) {
      try {
        const decoded = jwtDecode<Omit<TokenPayload, 'role'> & { role?: unknown }>(
          newToken,
        );
        const normalizedRole = normalizeRole(decoded.role);
        setUser(
          normalizedRole
            ? {
                ...decoded,
                role: normalizedRole,
              }
            : null,
        );
        if (decoded.tenantSlug) {
          saveTenantSlug(decoded.tenantSlug);
        }
      } catch (e) {
        console.warn('[Auth] Failed to decode token. Error:', e);
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  // Membership roles ranked so we can pick the most capable tenant on sign-in.
  const ROLE_RANK: Record<string, number> = { OPERATOR: 3, ADMIN: 2, USER: 1 };

  /**
   * The backend picks `memberships[0]` when no tenant is supplied at login, which is
   * effectively arbitrary. A user who is ADMIN of one tenant and a plain member of
   * another can therefore land in the member tenant and be denied their own admin
   * pages. Re-select the highest-privilege membership and swap the token for one
   * scoped to it. Ties are broken by the tenant the user last worked in.
   */
  const reconcilePreferredTenant = async (currentToken: string) => {
    const apiBase = process.env.NEXT_PUBLIC_API_BASE;
    if (!apiBase) return;

    try {
      const decoded = jwtDecode<Omit<TokenPayload, 'role'> & { role?: unknown }>(currentToken);
      // Operators resolve to OPERATOR in every tenant, so there is nothing to improve.
      if (normalizeRole(decoded.role) === 'OPERATOR') return;

      const res = await fetch(`${apiBase}/users/memberships`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        credentials: 'include',
      });
      if (!res.ok) return;

      const memberships = (await res.json()) as
        | { tenantSlug: string; tenantName?: string; role: string }[]
        | null;
      if (!Array.isArray(memberships) || memberships.length < 2) return;

      const remembered = getActiveTenantSlug();
      const ranked = [...memberships].sort((a, b) => {
        const byRole = (ROLE_RANK[b.role] ?? 0) - (ROLE_RANK[a.role] ?? 0);
        if (byRole !== 0) return byRole;
        if (a.tenantSlug === remembered) return -1;
        if (b.tenantSlug === remembered) return 1;
        return (a.tenantName ?? a.tenantSlug).localeCompare(b.tenantName ?? b.tenantSlug);
      });

      const preferred = ranked[0];
      if (!preferred || preferred.tenantSlug === decoded.tenantSlug) return;

      const switched = await fetch(`${apiBase}/auth/switch-tenant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
        credentials: 'include',
        body: JSON.stringify({ tenantSlug: preferred.tenantSlug }),
      });
      if (!switched.ok) return;

      const data = await switched.json();
      if (data?.access_token) {
        console.log('[Auth] Reconciled active tenant to', preferred.tenantSlug);
        setToken(data.access_token);
      }
    } catch (err) {
      // Never block sign-in on this; the user can still switch tenant manually.
      console.warn('[Auth] Tenant reconcile failed', err);
    }
  };

  const login = async (
    newToken: string,
    options?: { reconcileTenant?: boolean },
  ) => {
    setToken(newToken);
    if (options?.reconcileTenant === false) return;
    await reconcilePreferredTenant(newToken);
  };

  const refreshAccessToken = async () => {
    try {
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.access_token) {
          setToken(data.access_token);
        }
      }
    } catch (err) {
      console.warn('[Auth] Manual refresh failed', err);
    }
  };

  const logout = async () => {
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.warn('[Auth] Logout request failed', err);
    }

    setToken(null);
    // Tenant selection is per-session; leaving it behind makes the next account
    // sign in against the previous account's tenant.
    clearTenantStorage();
    window.location.href = '/login';
  };

  // ⏳ Try to refresh token on app load
  useEffect(() => {
    const tryRefresh = async () => {
      try {
        const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && data.access_token) {
          setToken(data.access_token);
        } else {
          console.warn('[Auth] Refresh failed');
          setToken(null);
        }
      } catch (err) {
        console.error('[Auth] Refresh request error', err);
        setToken(null);
      } finally {
        setChecking(false); // ✅ Ready to render
      }
    };
    tryRefresh();
  }, []);

  // 🔁 Register external setter
  useEffect(() => {
    registerTokenSetter(setToken);

    const handleTokenRefresh = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail) {
        setToken(customEvent.detail);
      }
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
    };
  }, []);

  const switchTenant = useCallback(
    async (slug: string) => {
      const base = process.env.NEXT_PUBLIC_API_BASE;
      if (!base) throw new Error('API base URL is not configured');
      const res = await fetchWithAuth(`${base}/auth/switch-tenant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantSlug: slug || null }),
      });
      if (!res.ok) throw await parseSwitchError(res);
      const data = await res.json();
      if (!data?.access_token) throw new Error('No access token returned');
      // An explicit switch must not be undone by the sign-in tenant reconcile.
      await login(data.access_token, { reconcileTenant: false });
    },
    [login],
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token,
        token,
        user,
        login,
        logout,
        refreshAccessToken,
        switchTenant,
      }}
    >
      {!checking && children}
    </AuthContext.Provider>
  );
};

// External access to token setter
let externalSetToken: ((token: string | null) => void) | null = null;

export const registerTokenSetter = (setter: (token: string | null) => void) => {
  externalSetToken = setter;
};

export const setGlobalAccessToken = (token: string | null) => {
  if (externalSetToken) {
    externalSetToken(token);
  }
};

export const useAuth = () => useContext(AuthContext);
